import {
  CTCSS_TONES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const MEMORY_SIZE = 0x4e13
const CHANNEL_COUNT = 500
const PRESENT_START = 0x0050
const SCANNED_START = 0x0090
const CHANNEL_START = 0x00d0
const CHANNEL_SIZE = 32
const VALID_TONES = [62.5, ...CTCSS_TONES]
const VALID_DCS = Array.from({ length: 778 }, (_, index) => index).filter(
  (value) => !value.toString().includes('8') && !value.toString().includes('9'),
)
const TUNING_STEPS = [2.5, 5, 6.25, 10, 12.5, 20, 25, 30, 50]
const READ_MAP = [
  [0x2000, 0x2000, 0x01],
  [0x12000, 0x12000, 0x01],
  [0x22000, 0x22001, 0x01],
  [0x32000, 0x32001, 0x01],
  [0x42000, 0x420c7, 0x01],
  [0x52000, 0x520c7, 0x01],
  [0x62000, 0x62067, 0x01],
  [0x1002000, 0x1002000, 0x01],
  [0x2001000, 0x200100f, 0x01],
  [0x2011000, 0x2091000, 0x10000],
  [0x3001000, 0x3091000, 0x10000],
  [0x4000500, 0x400057e, 0x01],
  [0x4010800, 0x401087e, 0x01],
  [0x5001000, 0x500101f, 0x01],
  [0x5012000, 0x5012000, 0x01],
  [0x5021000, 0x5021000, 0x01],
  [0x6002000, 0x600200f, 0x01],
  [0x6012000, 0x6012000, 0x01],
  [0x6021000, 0x6021000, 0x01],
  [0x6032000, 0x6032006, 0x01],
  [0x8002000, 0x8002001, 0x01],
] as const
const WRITE_MAP = [
  [0x2000, 0x2000, 1, 0x0010],
  [0x12000, 0x12000, 1, 0x30],
  [0x22000, 0x22001, 1, 0x50],
  [0x32000, 0x32001, 1, 0x90],
  [0x42000, 0x420c7, 1, 0xd0],
  [0x52000, 0x520c7, 1, 0x19d0],
  [0x62000, 0x62067, 1, 0x32d0],
  [0x1002000, 0x1002000, 1, 0x3fd0],
  [0x2001000, 0x200100f, 1, 0x3ff0],
  [0x2011000, 0x2021000, 0x10000, 0x40f0],
  [0x2031000, 0x2041000, 0x10000, 0x4110],
  [0x2071000, 0x2081000, 0x10000, 0x4150],
  [0x3011000, 0x3021000, 0x10000, 0x4190],
] as const

type RA25Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  radioType: string
}

export type RA25Profile = {
  protocol: 'retevis-ra25'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 115200
  channelMinimum: 1
  channelMaximum: number
  nameLength: 8
  memorySize: number
  radioType: string
  powerLevels: readonly ['Low', 'Mid', 'High']
}

const SPECS: readonly RA25Spec[] = [
  {
    id: 'retevis-ra25',
    vendor: 'Retevis',
    model: 'RA25',
    summary: 'Retevis RA25 dual-band mobile.',
    radioType: 'RA_25UV',
  },
  {
    id: 'anytone-779uv',
    vendor: 'AnyTone',
    model: '779UV',
    summary: 'AnyTone 779UV dual-band mobile.',
    radioType: 'AT779UV',
  },
  {
    id: 'radioddity-db20-g',
    vendor: 'Radioddity',
    model: 'DB20-G',
    summary: 'Radioddity DB20-G dual-band mobile.',
    radioType: 'AT779UV',
  },
]

export const RA25_PROFILES: readonly RA25Profile[] = SPECS.map((spec) => ({
  protocol: 'retevis-ra25',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '500 channels', '8-character names'],
  baudRate: 115200,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: MEMORY_SIZE,
  radioType: spec.radioType,
  powerLevels: ['Low', 'Mid', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function decodeBbcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = length - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function readUint16BE(raw: Uint8Array, offset: number): number {
  return (raw[offset] << 8) | raw[offset + 1]
}

function writeUint16BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 8) & 0xff
  raw[offset + 1] = value & 0xff
}

function statusBit(image: Uint8Array, start: number, number: number): boolean {
  return (image[start + Math.floor(number / 8)] & (1 << (number % 8))) !== 0
}

function setStatusBit(image: Uint8Array, start: number, number: number, value: boolean): void {
  const offset = start + Math.floor(number / 8)
  const mask = 1 << (number % 8)
  image[offset] = value ? image[offset] | mask : image[offset] & ~mask
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 0x1000 && value < 0x2000) {
    return {
      mode: 'ctcss',
      ctcss: VALID_TONES[value - 0x1000] ?? null,
      dtcs: null,
      polarity: 'N',
    }
  }
  if (value >= 0x2000 && value < 0x3000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: VALID_DCS[value - 0x2000] ?? null,
      polarity: 'N',
    }
  }
  if (value >= 0x3000 && value < 0x3200) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: VALID_DCS[value - 0x3000] ?? null,
      polarity: 'R',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = VALID_TONES.indexOf(
      channel.ctcss as (typeof VALID_TONES)[number],
    )
    if (index >= 0) return index + 0x1000
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = VALID_DCS.indexOf(channel.dtcs)
    if (index >= 0) return index + (channel.dtcsPolarity === 'R' ? 0x3000 : 0x2000)
  }
  return 0xffff
}

function decodeName(raw: Uint8Array): string {
  const allowed = (char: string) =>
    char.charCodeAt(0) >= 32 && char.charCodeAt(0) <= 126 && !'\\`~'.includes(char)
  return String.fromCharCode(
    ...raw.map((value) => (value === 0 || value === 0xff ? 0x20 : value)),
  ).split('').filter(allowed).join('').trim()
}

export function parseRA25Channel(
  image: Uint8Array,
  profile: RA25Profile,
  number: number,
): WebChannel {
  if (!statusBit(image, PRESENT_START, number - 1)) return emptyChannel(number)
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxFrequency = decodeBbcd(raw.slice(0, 4)) * 10
  if (!rxFrequency) return emptyChannel(number)
  const offset = decodeBbcd(raw.slice(4, 8)) * 10
  const duplex = raw[10] & 0x03
  const txDisabled = (raw[9] & 0x01) !== 0
  const txFrequency = txDisabled
    ? null
    : duplex === 2
      ? rxFrequency - offset
      : duplex === 3
        ? rxFrequency + offset
        : rxFrequency
  const txTone = decodeTone(readUint16BE(raw, 16))
  const rxTone = decodeTone(readUint16BE(raw, 14))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(19, 27)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[9] & 0x04 ? 'NFM' : 'FM',
    power: profile.powerLevels[(raw[10] >> 2) & 0x03] ?? 'Low',
    skip: !statusBit(image, SCANNED_START, number - 1),
  }
}

export function parseRA25Channels(
  image: Uint8Array,
  profile: RA25Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseRA25Channel(image, profile, index + 1),
  )
}

export function applyRA25Channel(
  image: Uint8Array,
  profile: RA25Profile,
  channel: WebChannel,
): void {
  setStatusBit(image, PRESENT_START, channel.number - 1, !channel.empty)
  setStatusBit(
    image,
    SCANNED_START,
    channel.number - 1,
    !channel.empty && !channel.skip,
  )
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0)) raw.fill(0)
  encodeBbcd(Math.round(channel.rxFrequency / 10), 4).forEach(
    (value, index) => {
      raw[index] = value
    },
  )
  const tx = channel.txFrequency
  const duplex = tx ? (tx > channel.rxFrequency ? 3 : tx < channel.rxFrequency ? 2 : 0) : 0
  const offset = tx ? Math.abs(tx - channel.rxFrequency) : 0
  encodeBbcd(Math.round(offset / 10), 4).forEach((value, index) => {
    raw[4 + index] = value
  })
  raw[8] = TUNING_STEPS.indexOf(channel.tuningStep ?? 5)
  raw[9] =
    (raw[9] & 0xfa) |
    (tx ? 0 : 0x01) |
    (channel.mode === 'NFM' ? 0x04 : 0)
  raw[10] =
    (raw[10] & 0xf0) |
    ((
      Math.max(
        0,
        profile.powerLevels.indexOf(
          channel.power as (typeof profile.powerLevels)[number],
        ),
      ) & 0x03
    ) << 2) |
    duplex
  const tone = encodeTone(channel)
  writeUint16BE(raw, 14, tone)
  writeUint16BE(raw, 16, tone)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .replace(/[\\`~]/g, '')
    .slice(0, 8)
    .padEnd(8, ' ')
  for (let index = 0; index < 8; index += 1) {
    raw[19 + index] = name.charCodeAt(index)
  }
}

function blockLength(endAddress: number): number {
  return ((endAddress & 0xf000) + (endAddress & 0x0f00)) >>> 8
}

async function send(
  transport: RadioTransport,
  command: Uint8Array,
  expected: number,
): Promise<Uint8Array> {
  await transport.write(command)
  const response = await transport.readExact(expected, 3500)
  if (expected > 6) {
    const checksum = response.slice(0, -1).reduce((sum, value) => (sum + value) & 0xff, 0)
    if (checksum !== response.at(-1)) throw new Error('RA25 response checksum failed.')
    return response.slice(5, -1)
  }
  return response
}

async function enter(
  profile: RA25Profile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The RA25 refused programming mode.')
  }
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(16, 3500)
  const radioType = new TextDecoder().decode(identity.slice(1, 8)).replace(/\u0000/g, '')
  const version = new TextDecoder().decode(identity.slice(9, 13))
  if (radioType !== profile.radioType || version !== 'V200') {
    throw new Error(
      `The radio identifies as ${radioType} ${version}, not ${profile.model}.`,
    )
  }
  return identity
}

async function finish(transport: RadioTransport): Promise<void> {
  try {
    await send(transport, new TextEncoder().encode('END'), 1)
  } catch {
    // Some firmware revisions reboot without acknowledging END.
  }
}

export async function readRA25RadioWithTransport(
  profile: RA25Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const identity = await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    image.set(identity, 0)
    let offset = identity.length
    for (const [start, end, step] of READ_MAP) {
      const length = blockLength(end)
      for (let address = start; address <= end; address += step) {
        const command = new Uint8Array(5)
        command[0] = 0x52
        command[1] = (address >>> 24) & 0xff
        command[2] = (address >>> 16) & 0xff
        command[3] = (address >>> 8) & 0xff
        command[4] = address & 0xff
        const data = await send(transport, command, length + 6)
        image.set(data, offset)
        offset += length
        onProgress?.({
          current: offset,
          total: MEMORY_SIZE,
          label: 'Reading Retevis RA25 memory',
        })
      }
    }
    return { image, reportedModel: profile.model }
  } finally {
    await finish(transport)
  }
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const packet = new Uint8Array(5 + data.length + 1)
  packet[0] = 0x57
  packet[1] = (address >>> 24) & 0xff
  packet[2] = (address >>> 16) & 0xff
  packet[3] = (address >>> 8) & 0xff
  packet[4] = address & 0xff
  packet.set(data, 5)
  packet[packet.length - 1] =
    packet.slice(0, -1).reduce((sum, value) => (sum + value) & 0xff, 0)
  await transport.write(packet)
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error(`The RA25 rejected write block 0x${address.toString(16)}.`)
  }
}

export async function writeRA25RadioWithTransport(
  profile: RA25Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than RA25 memory.')
  }
  const identity = await enter(profile, transport)
  if (decodeBbcd(identity.slice(8, 9)) !== decodeBbcd(image.slice(8, 9))) {
    throw new Error('The radio mode does not match the loaded image.')
  }
  try {
    const writes: Array<{ address: number; offset: number; length: number }> = []
    for (const [start, end, step, imageStart] of WRITE_MAP) {
      const length = blockLength(end)
      let index = 0
      for (let address = start; address <= end; address += step) {
        writes.push({
          address,
          offset: imageStart + index * length,
          length,
        })
        index += 1
      }
    }
    for (let index = 0; index < writes.length; index += 1) {
      const write = writes[index]
      await writeBlock(
        transport,
        write.address,
        image.slice(write.offset, write.offset + write.length),
      )
      onProgress?.({
        current: index + 1,
        total: writes.length,
        label: 'Writing Retevis RA25 memory',
      })
    }
  } finally {
    await finish(transport)
  }
}
